/**
 * The bell's dropdown body: the 5 latest open alerts (each opens its record,
 * or the Alerts page when it points at nothing) and "View all alerts".
 * Loads only while the menu is open.
 */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AlertView } from '@nord-living/shared';

import { LoadingSpinner } from '@/components/ui';

import { ALERT_ICONS, alertHref } from '../alertMeta';
import { useAlerts } from '../hooks/useAlerts';

const ITEM = 'flex cursor-pointer select-none items-start gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground';

function MenuAlert({ alert }: { alert: AlertView }) {
  const { t } = useTranslation('alerts');
  const Icon = ALERT_ICONS[alert.type];
  return (
    <DropdownMenu.Item asChild className={ITEM}>
      <Link to={alertHref(alert.entity) ?? '/alerts'}>
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0">
          <span className="block font-medium leading-snug">{t(`types.${alert.type}`)}</span>
          <span className="block truncate text-xs text-muted-foreground">{alert.title}</span>
        </span>
      </Link>
    </DropdownMenu.Item>
  );
}

export function AlertBellMenu({ open }: { open: boolean }) {
  const { t } = useTranslation('alerts');
  const { data, isLoading } = useAlerts({ status: 'open', limit: 5 }, open);
  const items = data?.items ?? [];
  return (
    <>
      <p className="border-b px-2.5 py-2 font-display text-sm font-semibold">{t('bell.heading')}</p>
      <div className="py-1">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" />
          </div>
        ) : null}
        {!isLoading && !items.length ? <p className="px-2.5 py-3 text-sm text-muted-foreground">{t('bell.empty')}</p> : null}
        {items.map((a) => (
          <MenuAlert key={a.id} alert={a} />
        ))}
      </div>
      <DropdownMenu.Separator className="my-1 h-px bg-border" />
      <DropdownMenu.Item asChild className={ITEM}>
        <Link to="/alerts" className="font-medium text-primary">
          {t('bell.viewAll')}
          <ArrowRight className="ml-auto h-4 w-4 self-center" aria-hidden />
        </Link>
      </DropdownMenu.Item>
    </>
  );
}
